'use client'

import { useQuery } from '@tanstack/react-query'

interface PricingTier {
  min: number
  max: number
  price: number
  currency: string
}

interface PricingPlan {
  id: string
  name: string
  display_name: string
  min_employees: number
  max_employees: number
  description: string
  features: any
  tiers: PricingTier[]
}

interface Currency {
  symbol: string
  rate: number
  name: string
}

interface PricingData {
  plans: Record<string, PricingPlan>
  currencies: Record<string, Currency>
  last_updated: string
  cached?: boolean
  cache_age?: number
}

interface PricingResponse {
  success: boolean
  data: PricingData
}

const fetchPricingData = async (): Promise<PricingData> => {
  const response = await fetch('/api/hr/pricing/calculator?action=get_plans')
  const result: PricingResponse = await response.json()
  
  if (!response.ok || !result.success) {
    throw new Error(result.data ? 'Failed to fetch pricing data' : 'API Error')
  }
  
  return result.data
}

export const usePricingData = () => {
  return useQuery({
    queryKey: ['pricing-data'],
    queryFn: fetchPricingData,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

// Hook for calculating prices with cached data
export const useCalculatePrice = () => {
  const { data: pricingData } = usePricingData()

  const calculatePrice = async (plan: string, employees: number, currency: string = 'eur') => {
    // If we have cached pricing data, try to calculate locally first
    if (pricingData?.plans[plan]) {
      const planData = pricingData.plans[plan]
      const currencyData = pricingData.currencies[currency.toLowerCase()]
      
      if (currencyData && employees >= planData.min_employees && employees <= planData.max_employees) {
        // Find the right pricing tier
        const tier = planData.tiers.find(t => employees >= t.min && employees <= t.max)
        if (tier) {
          const basePrice = tier.price
          const totalPrice = employees * basePrice * currencyData.rate
          
          return {
            success: true,
            data: {
              plan_name: planData.display_name,
              plan_key: plan,
              employees: employees,
              price_per_employee: basePrice * currencyData.rate,
              total_price: totalPrice,
              onboarding_fee: 0, // We'll still need API for onboarding fees
              currency_symbol: currencyData.symbol,
              currency_code: currency.toUpperCase(),
              tier_info: {
                min: tier.min,
                max: tier.max,
                base_price: basePrice
              },
              cached_calculation: true
            }
          }
        }
      }
    }
    
    // Fall back to API call
    const response = await fetch(`/api/hr/pricing/calculator?action=calculate&plan=${plan}&employees=${employees}&currency=${currency}`)
    return await response.json()
  }

  return { calculatePrice }
}

// Hook for invalidating pricing cache
export const useInvalidatePricingCache = () => {
  const invalidateCache = async () => {
    // Invalidate server cache
    await fetch('/api/hr/pricing/calculator?action=invalidate_cache')
    
    // Invalidate client cache
    // This would need to be called from a component that has access to queryClient
    // We'll handle this in the component that uses it
  }

  return { invalidateCache }
}